'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
import { useWallet } from '@/hooks/useWallet';
import { useSelectedRpc } from '@/hooks/useSelectedRpc';
import { useRpcPatcher } from '@/hooks/useRpcPatcher';
import { rpcManager } from '@/services/rpcManager';
import toast, { Toaster } from 'react-hot-toast';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useChainStore } from '@/stores/chainStore';
import Link from 'next/link';

// Components
import ChainSelector from '@/components/ChainSelector';
import RpcTester from '@/components/RpcTester';
import AccountCard from '@/components/wallet/AccountCard';
import BalanceCard from '@/components/wallet/BalanceCard';
import SendTransaction from '@/components/wallet/SendTransaction';
import TokenList from '@/components/wallet/TokenList';
import TransactionHistory from '@/components/wallet/TransactionHistory';

// Icons
import {
  WalletIcon,
  KeyIcon,
  SparklesIcon,
  ShieldCheckIcon,
  BoltIcon,
  FireIcon,
  GlobeAltIcon,
  CubeIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

// Types
interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  color: string;
}

interface Stat {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

// Constants
const FEATURES: Feature[] = [
  {
    icon: ShieldCheckIcon,
    title: 'Secure & Private',
    description: 'Your keys, your crypto. Non-custodial wallet with industry-leading security.',
    color: 'text-green-600 dark:text-green-400'
  },
  {
    icon: BoltIcon,
    title: 'Lightning Fast',
    description: 'Automatic RPC failover ensures the fastest transaction experience.',
    color: 'text-blue-600 dark:text-blue-400'
  },
  {
    icon: GlobeAltIcon,
    title: 'Multi-Chain Support',
    description: 'Support for 100+ EVM chains with more coming soon.',
    color: 'text-purple-600 dark:text-purple-400'
  },
  {
    icon: WalletIcon,
    title: 'User Friendly',
    description: 'Clean interface designed for both beginners and power users.',
    color: 'text-orange-600 dark:text-orange-400'
  }
];

export default function HomePage() {
  // Hooks
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  
  // Chain store
  const { getRpcHealth, setLastSelectedChain } = useChainStore();
  
  // Get RPC management
  const { selectedRpc, setSelectedRpc: onSelectRpc } = useSelectedRpc(chainId);
  const [rpcs, setRpcs] = useState<string[]>([]);
  const [isTestingRpc, setIsTestingRpc] = useState(false);
  const [rpcHealth, setRpcHealth] = useState<any>(null);
  
  // Auto-open wallet if coming from import
  const [autoOpenWallet, setAutoOpenWallet] = useState(false);
  
  // Check for openWallet parameter
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('openWallet') === 'true') {
        setAutoOpenWallet(true);
        // Clean up URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);
  
  // Auto-scroll to wallet section when coming from import
  useEffect(() => {
    if (autoOpenWallet && isConnected) {
      // Small delay to ensure wallet is loaded and avoid toaster conflict
      setTimeout(() => {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          toast.success('Welcome to your wallet! 🎉');
        });
      }, 1500); // Increased delay to ensure wallet is fully loaded
      setAutoOpenWallet(false);
    }
  }, [autoOpenWallet, isConnected]);
  
  // Auto-open wallet when coming from import and wallet is connected
  useEffect(() => {
    if (autoOpenWallet && isConnected && address) {
      console.log('🎉 Auto-opening wallet dashboard for:', address);
      // The dashboard will automatically render when isConnected is true
    }
  }, [autoOpenWallet, isConnected, address]);
  
  // Patch wagmi transport when RPC changes
  useRpcPatcher(chainId, selectedRpc || undefined);
  
  // Load RPCs for current chain
  useEffect(() => {
    if (chainId) {
      rpcManager.getRpcsForChain(chainId).then(setRpcs);
    }
  }, [chainId]);

  // Clean up any leftover pending chain switch from old version
  useEffect(() => {
    localStorage.removeItem('helio-pending-chain-switch');
  }, []);
  
  // Test best RPC
  const testBestRpc = useCallback(async () => {
    if (!chainId || rpcs.length === 0) return;
    
    setIsTestingRpc(true);
    try {
      // Test each RPC
      const results = await Promise.all(
        rpcs.map(async (rpc) => {
          const health = await rpcManager.testRpc(rpc, chainId);
          return { ...health, url: rpc };
        })
      );
      
      const best = results
        .filter((r: any) => r.latency < Infinity)
        .sort((a: any, b: any) => a.latency - b.latency)[0];
      
      if (best) {
        onSelectRpc(best.url);
        toast.success(`Best RPC: ${new URL(best.url).hostname} (${best.latency}ms)`);
      }
    } finally {
      setIsTestingRpc(false);
    }
  }, [chainId, rpcs, onSelectRpc]);
  
  // Get current RPC health
  const getCurrentRpcHealth = useCallback(async () => {
    if (!selectedRpc || !chainId) return null;
    
    const health = await rpcManager.testRpc(selectedRpc, chainId);
    setRpcHealth(health);
    return health;
  }, [selectedRpc, chainId]);
  
  // Test RPC health on change
  useEffect(() => {
    if (selectedRpc && chainId) {
      getCurrentRpcHealth();
    }
  }, [selectedRpc, chainId, getCurrentRpcHealth]);
  
  // Wallet hook
  const {
    chainId: walletChainId,
    chainInfo,
    balance,
    tokens,
    transactions,
    isLoadingBalance,
    onSend,
    onSendToken,
    reloadBalance,
    preferences,
    onUpdateTransactionStatus,
    onRemoveTransaction,
    onClearTransactions,
    onAddTransactions
  } = useWallet();

  // State
  const [dialogs, setDialogs] = useState({
    chainSelector: false,
    rpcTester: false,
    send: false
  });
  const [selectedTokenForSend, setSelectedTokenForSend] = useState<any>(null);

  // Effects
  // Handle dialog state
  const openDialog = useCallback((dialog: keyof typeof dialogs) => {
    setDialogs(prev => ({ ...prev, [dialog]: true }));
  }, []);

  const closeDialog = useCallback((dialog: keyof typeof dialogs) => {
    setDialogs(prev => ({ ...prev, [dialog]: false }));
  }, []);

  const handleSelectRpc = useCallback((rpc: string, chainId?: number) => {
    onSelectRpc(rpc);
  }, [onSelectRpc]);

  const handleTokenSelect = useCallback((token: any) => {
    setSelectedTokenForSend(token);
  }, []);

  // Render functions
  const renderHeroSection = () => (
    <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-5"></div>
      
      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium mb-6">
            <SparklesIcon className="h-4 w-4" />
            <span>100+ Chains Supported</span>
          </div>
          
          {/* Title */}
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl lg:text-7xl">
            Your Gateway to
            <span className="block mt-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient">
              Multi-Chain DeFi
            </span>
          </h1>
          
          {/* Description */}
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            HelioWallet is a secure, non-custodial wallet that puts you in complete control 
            of your crypto assets across multiple blockchains. Import or create wallets, 
            manage tokens, and interact with DeFi protocols seamlessly.
          </p>
          
          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-x-6">
            <ConnectButton />
            <Link
              href="/import-wallet"
              className="group flex items-center gap-2 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200"
            >
              <KeyIcon className="h-5 w-5 group-hover:rotate-12 transition-transform" />
              Import Existing Wallet
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-12 flex items-center justify-center gap-8 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Non-Custodial</span>
            </div>
            <div className="flex items-center gap-2">
              <BoltIcon className="h-5 w-5" />
              <span>Fast & Reliable</span>
            </div>
            <div className="flex items-center gap-2">
              <GlobeAltIcon className="h-5 w-5" />
              <span>Multi-Chain</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderFeaturesSection = () => (
    <section className="py-24 bg-white dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Everything you need in a Web3 wallet
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
            Built with security, performance, and user experience in mind
          </p>
        </div>
        
        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="group relative"
            >
              {/* Hover effect background */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-100 transition duration-300 blur-lg"></div>
              
              {/* Card content */}
              <div className="relative h-full p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl transition-all duration-200 group-hover:bg-white dark:group-hover:bg-gray-900">
                <feature.icon className={`h-12 w-12 ${feature.color} mb-4`} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCTASection = () => (
    <section className="relative py-16 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
      
      {/* Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-10"></div>
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Ready to take control of your crypto?
        </h2>
        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
          Join thousands of users who trust HelioWallet for their Web3 journey. 
          No registration, no personal data, just pure decentralization.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button
                onClick={openConnectModal}
                className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Get Started Now
              </button>
            )}
          </ConnectButton.Custom>
          <Link
            href="/import-wallet"
            className="px-8 py-3 bg-transparent text-white border-2 border-white rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200"
          >
            Import Wallet
          </Link>
        </div>
      </div>
    </section>
  );

  const renderDashboard = () => (
    <div className="min-h-[calc(100vh-8rem)] bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <AccountCard
            chainInfo={chainInfo}
            selectedRpc={selectedRpc}
            rpcHealth={rpcHealth}
            onOpenChainSelector={() => openDialog('chainSelector')}
            onOpenRpcDialog={() => openDialog('rpcTester')}
            onTestBestRpc={testBestRpc}
            isTestingRpc={isTestingRpc}
          />
          
          <BalanceCard
            chainId={chainId}
            isLoadingBalance={isLoadingBalance}
            onReloadBalance={reloadBalance}
            tokenCount={tokens.length}
          />
        </div>

        {/* Send & Tokens Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-7">
            <SendTransaction
              balance={balance}
              tokens={tokens}
              chainId={chainId}
              onSend={onSend}
              onSendToken={onSendToken}
              selectedToken={selectedTokenForSend}
            />
          </div>

          <div className="lg:col-span-5">
            <TokenList 
              onTokenSelect={handleTokenSelect} 
              selectedToken={selectedTokenForSend}
            />
          </div>
        </div>

        {/* Transaction History */}
        <div className="mb-6">
          <TransactionHistory
            transactions={transactions as any}
            chainInfo={chainInfo}
            chainId={walletChainId || chainId}
            address={address}
            onUpdateTransactionStatus={onUpdateTransactionStatus}
            onRemoveTransaction={onRemoveTransaction}
            onClearTransactions={onClearTransactions}
            onAddTransactions={onAddTransactions}
          />
        </div>

      </div>
    </div>
  );

  // Main render
  return (
    <>
      <Toaster position="top-right" />
      
      {!isConnected ? (
        <div className="min-h-[calc(100vh-8rem)]">
          {renderHeroSection()}
          {renderFeaturesSection()}
          {renderCTASection()}
        </div>
      ) : (
        renderDashboard()
      )}
      
      {/* Dialogs */}
      <ChainSelector 
        open={dialogs.chainSelector} 
        onClose={() => closeDialog('chainSelector')} 
      />

      <RpcTester
        open={dialogs.rpcTester}
        onClose={() => closeDialog('rpcTester')}
        chainId={chainId || 1}
        rpcs={rpcs}
        selectedRpc={selectedRpc}
        onSelectRpc={handleSelectRpc}
      />
    </>
  );
}

// Add some custom styles for animations
const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .animate-blob {
    animation: blob 7s infinite;
  }
  
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  
  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient 6s ease infinite;
  }
`;

// Inject custom styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = customStyles;
  document.head.appendChild(style);
}